/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import { Box, Button, Checkbox, IconButton, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import appTheme from '../../common/theme/appTheme.json';
import ClearIcon from '@mui/icons-material/Clear';
import { BeamAutocomplete } from '../../common/formik/BeamAutocomplete';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import AddIcon from '@mui/icons-material/Add';
import React, { useEffect, useState } from 'react';
import { HnaNetworkApprovalState, HnaZone } from '../../../shared/entities/hnaRequest';
import { useFormikContext } from 'formik';
import TriStateCheckbox, { TriState } from '../../common/buttons/TriStateCheckbox';

import { v4 as generateUuid } from 'uuid';

export const useStyles = makeStyles({
  delete: {
    flexBasis: '5%',
  },
  dropdown: {
    flexBasis: '25%',
    color: appTheme.primaryTextColor,
    fontFamily: appTheme.appFontFamily,
  },
  checkbox: {
    flexBasis: '10%',
    textAlign: 'center',
  },
  headerText: {
    fontSize: '12px',
    color: appTheme.primaryTextColor,
    fontFamily: appTheme.appFontFamily,
  },
  countryText: {
    color: appTheme.primaryTextColor,
    fontFamily: appTheme.appFontFamily,
  },
});

export const networkTypes = [
  {
    name: 'HCX Overflight',
    label: 'HCX Overflight',
    subLabel: '↑ 10,000 ft',
  },
  {
    name: 'HCX Ground',
    label: 'HCX Ground',
    subLabel: '↓ 10,000 ft',
  },
  {
    name: 'AL Overflight',
    label: 'AL Overflight',
    subLabel: '↑ 10,000 ft',
  },
  {
    name: 'AL Ground',
    label: 'AL Ground',
    subLabel: '↓ 10,000 ft',
  },
  {
    name: 'VI Overflight',
    label: 'VI Overflight',
    subLabel: '↑ 10,000 ft',
  },
  {
    name: 'VI Ground',
    label: 'VI Ground',
    subLabel: '↓ 10,000 ft',
  },
];

export const HnaZoneSelector = (props: any) => {
  const formikContext = useFormikContext();
  const classes = useStyles();
  const [rows, setRows] = useState<HnaZone[]>([]);
  const [availableCountries, setAvailableCountries] = useState<string[]>([]);

  useEffect(() => {
    setAvailableCountries(props.countries.filter((c: string) => !rows.map(a => a.country).includes(c)));
  }, [props.countries, rows]);

  // when changing country selection on a dropdown
  const changeRow = (oldRow: HnaZone, newRow: HnaZone) => {
    let rowCopy = rows.slice();
    rowCopy[rowCopy.map(e => e.country).indexOf(oldRow.country)] = newRow;
    setRows(rowCopy);
  };

  // when deleting row via the X icon
  const removeRow = (row: HnaZone) => {
    let rowCopy = rows.slice();
    rowCopy.splice(rowCopy.map(e => e.country).indexOf(row.country), 1);
    setRows(rowCopy);
  };

  // when checking a box
  const updateRow = (row: HnaZone, event: React.ChangeEvent<HTMLInputElement>) => {
    let rowCopy = rows.slice();
    let targetRow = rowCopy[rowCopy.map(e => e.country).indexOf(row.country)];
    if (event.target.checked) {
      targetRow.networks = [
        ...targetRow.networks,
        { network: event.target.value, state: HnaNetworkApprovalState.REQUESTED },
      ];
    } else {
      targetRow.networks.splice(targetRow.networks.map(e => e.network).indexOf(event.target.value), 1);
    }
    setRows(rowCopy);
  };

  // when checking a TriStateCheckbox
  const updateTriRow = (row: HnaZone, network: string, state: HnaNetworkApprovalState) => {
    let rowCopy = rows.slice();
    let targetRow = rowCopy[rowCopy.map(e => e.country).indexOf(row.country)];
    targetRow.networks[targetRow.networks.map(e => e.network).indexOf(network)] = { network: network, state: state };
    setRows(rowCopy);
  };

  useEffect(() => {
    formikContext.setFieldValue('hnaZones', rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  useEffect(() => {
    if (props.field.value && props.field.value.length > 0) {
      if (rows.length === 0 || (rows.length === 1 && rows[0].country.includes('row'))) {
        setRows(props.field.value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.field.value]);

  return (
    <Box display="flex" flexDirection="column" rowGap="15px">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box className={`${classes.dropdown} ${classes.headerText}`}>HNA Zone</Box>
        <Box className={classes.delete}></Box>
        {networkTypes.map(network => {
          return (
            <Box className={`${classes.checkbox} ${classes.headerText}`} key={network.name}>
              <Box textAlign="center">{network.label}</Box>
              <Box textAlign="center">{network.subLabel}</Box>
            </Box>
          );
        })}
      </Box>
      {rows?.map(row => {
        return (
          <HnaZoneRow
            triState={props.triState}
            updateTriRow={updateTriRow}
            key={row.country}
            currentCountry={row}
            availableCountries={availableCountries ? availableCountries : []}
            changeRow={changeRow}
            removeRow={removeRow}
            updateRow={updateRow}
          />
        );
      })}
      {/* When not in tri-state mode, user can add/remove countries. */}
      {!props.triState && (
        <Box display="flex" alignItems="center">
          <Button
            onClick={() => {
              // give the blank row a unique UUID so we can find it later in rows
              setRows(rows => [...rows, { country: 'row' + generateUuid(), networks: [] }]);
            }}
          >
            <AddIcon style={{ color: 'white', marginRight: '20px' }} />
            Add country
          </Button>
        </Box>
      )}

      {formikContext.errors && formikContext.errors['hnaZones'] ? (
        <Box color="#FA6F6A" fontSize="14px" textAlign={'center'} paddingBottom={'30px'}>
          {!Array.isArray(formikContext.errors['hnaZones'])
            ? 'Please select at least one country.'
            : 'Please select at least one network for each country.'}
        </Box>
      ) : (
        <></>
      )}
    </Box>
  );
};

export interface HnaZoneRowProp {
  triState?: boolean;
  updateTriRow?: any;
  currentCountry: HnaZone;
  availableCountries: string[];
  changeRow: (oldRow: HnaZone, newRow: HnaZone) => void;
  removeRow: (country: HnaZone) => void;
  updateRow: (country: HnaZone, event: React.ChangeEvent<HTMLInputElement>) => void;
}

const HnaZoneRow = ({
  triState,
  updateTriRow,
  currentCountry,
  availableCountries,
  changeRow = () => {},
  removeRow = () => {},
  updateRow = () => {},
}: HnaZoneRowProp) => {
  const classes = useStyles();
  const generateSelectedNetworks = () => {
    return currentCountry.networks.map(e => e.network);
  };
  const mapStatusToTriState = (currentNetwork: any) => {
    let selected = generateSelectedNetworks();
    let networks = currentCountry.networks;
    if (selected.includes(currentNetwork.name)) {
      switch (networks[networks.map(e => e.network).indexOf(currentNetwork.name)].state) {
        case HnaNetworkApprovalState.REQUESTED:
          return TriState.Blank;
        case HnaNetworkApprovalState.APPROVED:
          return TriState.Approved;
        case HnaNetworkApprovalState.DENIED:
          return TriState.Denied;
        default:
          return undefined;
      }
    } else {
      return undefined;
    }
  };

  const updateParent = (network: string, status: TriState) => {
    let state;
    if (status === 0) {
      state = HnaNetworkApprovalState.REQUESTED;
    } else if (status === 1) {
      state = HnaNetworkApprovalState.APPROVED;
    } else {
      state = HnaNetworkApprovalState.DENIED;
    }
    updateTriRow(currentCountry, network, state);
  };

  return (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      {triState ? (
        <></>
      ) : (
        <Box className={classes.delete}>
          <IconButton
            children={<ClearIcon />}
            onClick={() => {
              removeRow(currentCountry);
            }}
          />
        </Box>
      )}
      <Box className={classes.dropdown}>
        {triState ? (
          <Box className={classes.countryText}>{currentCountry.country}</Box>
        ) : (
          <BeamAutocomplete
            options={[...availableCountries, currentCountry.country].filter(e => !e.includes('row'))}
            size={'small'}
            value={currentCountry?.country.includes('row') ? undefined : currentCountry.country}
            autoComplete
            onChange={(_e, value) => {
              if (!value) {
                changeRow(currentCountry, { country: 'row' + generateUuid(), networks: [] });
              } else {
                changeRow(currentCountry, { country: value, networks: [] });
              }
            }}
            renderInput={params => <TextField {...params} placeholder={'Select a Country'} />}
          />
        )}
      </Box>
      {triState ? <Box className={classes.delete} /> : <></>}
      {networkTypes.map(network => {
        return (
          <Box className={classes.checkbox} key={network.name}>
            {!triState ? (
              <Checkbox
                disabled={currentCountry.country.includes('row')}
                sx={{ margin: 'auto', height: '30px' }}
                checkedIcon={<CheckBoxIcon style={{ color: '00C2DE' }} />}
                value={network.name}
                checked={generateSelectedNetworks().includes(network.name)}
                onChange={e => {
                  updateRow(currentCountry, e);
                }}
              />
            ) : (
              <TriStateCheckbox
                readOnly={false}
                status={mapStatusToTriState(network)}
                value={network.name}
                updateParent={updateParent}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};
